import { Subscription } from 'rxjs';
import {
    Activity,
    ActivityDetailVm,
    activityModelAddId,
    ActivityToCreate,
    createActivity,
    LockReasonCode,
} from '@/core/models/activity';
import { ActivityService } from '@/core/services/open-api/activity.service';
import { Tile } from '@/core/models/tile';
import { TileService } from '@/core/services/open-api/tile.service';
import { AnalyzeComponent } from '../../analyze.component';
import { LayerFilter, UserFilter } from '../../analyze-filters';
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { Layer } from '@/core/models/layer';
import { TileType } from '@/core/models/tile-type';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '@/core/services/open-api/user.service';
import { LoadingService } from '@/core/services/loading.service';
import { UserPermission } from '@/core/models/user-permission';
import { CurrentUser } from '@/core/models/user';
import { TimezonedDate } from '@/core/models/timezoned-date';
import { HttpErrorResponse } from '@angular/common/http';
import { AngularTranslations } from '@/core/types/angular-primeng-api';
import { ToastService } from '@/core/services/toast.service';
import { isEqual } from 'lodash-es';
import { CloneUtils } from '@/core/utils/clone-utils';
import { DataUtils } from '@/core/utils/data-utils';

@Component({
    selector: 'app-add-activity',
    templateUrl: './add-activity.component.html',
    styleUrls: ['./add-activity.component.scss'],
    standalone: false,
})
export class AddActivityComponent implements OnInit, OnDestroy, OnChanges {
    @Input()
    set selectedActivity(value: ActivityDetailVm | undefined) {
        this._selectedActivity = value;
        if (this.initialized) {
            this.prepareUI(true);
        }
    }

    @Input()
    set currentUserId(value: number | undefined) {
        this._currentUserId = value;
        if (value && this.initialized) {
            this.prepareUI(true);
        }
    }

    @Input() isNewActivityMode = false;
    @Input() showDialog = false;
    @Output() selectedActivityChange = new EventEmitter<ActivityDetailVm | undefined>();
    @Output() showDialogChange = new EventEmitter<boolean>();
    @Output() saved = new EventEmitter<number>();
    @Output() deleted = new EventEmitter<number>();
    layers: Layer[] = [];
    isLoading = true;
    usersFilter: UserFilter[] = [];
    layer1Filter: LayerFilter[] = [];
    layer2Filter: LayerFilter[] = [];
    layer3Filter: LayerFilter[] = [];
    selectedUserFilter?: UserFilter;
    selectedLayer1Filter?: Tile;
    selectedLayer2Filter?: Tile;
    selectedLayer3Filter?: Tile;
    assignedTiles: Tile[] = [];
    note = '';
    isBilled = false;
    isLocked = false;
    userCanOverrideLock = false;
    lockReasonCodes: LockReasonCode[] = [];
    disabledEditing = false;
    dateFrom: TimezonedDate = TimezonedDate.tzNow(-1, 'hour'); // TODO change type to TimezonedDate | null - the date picker can make the value null, if input is invalid...
    dateTo: TimezonedDate = TimezonedDate.tzNow(); // TODO change type to TimezonedDate | null - the date picker can make the value null, if input is invalid...
    fromOffset?: string;
    toOffset?: string;
    timeZoneSwitchText?: string;
    durationWarningText?: string;
    saveDisabled = false;
    currentUser?: CurrentUser;
    private _selectedActivity?: ActivityDetailVm;
    private _currentUserId?: number;
    isPro = false;
    userDateFormat = '';
    userHourFormat = '24';
    firstDayOfWeek = 0;
    private subscriptions = new Subscription();
    private initialized = false;
    dialogHeader = '';
    primaryButtonText = '';
    minDate: Date | undefined = undefined;

    constructor(
        private readonly analyzeComponent: AnalyzeComponent, // TODO remove this ref
        private readonly tileService: TileService,
        private readonly activityService: ActivityService,
        private readonly translate: TranslateService,
        public readonly userService: UserService,
        private readonly loadingService: LoadingService,
        private readonly toastService: ToastService,
        private readonly confirmationService: ConfirmationService,
    ) {}

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    ngOnInit(): void {
        // TODO See comment in analyze.component.ts for user loading. This only works correctly here,
        //     because the component is never shown on load.
        this.subscriptions.add(
            this.userService.currentUser$.subscribe((currentUser) => {
                this.currentUser = currentUser;
                this.isPro = currentUser.isPro();
                this.userDateFormat = currentUser.getDateFormat();
                this.userHourFormat = currentUser.timeFormat12or24();
                this.firstDayOfWeek = currentUser.getFirstDayOfWeek();
                if (currentUser.isAdmin() || currentUser.isManager()) {
                    this.minDate = undefined;
                } else {
                    // TODO implement later after account refactor
                    this.minDate = undefined;
                    /*
                    const minDate = new Date();
                    minDate.setDate(minDate.getDate() - currentUser...);
                    this.minDate = minDate;
                     */
                }
            }),
        );
        void this.initializeTiles();
        this.updateDialogHeader();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.onChangeHook(changes, 'ngOnChanges');
        this.updateDialogHeader();
    }

    private updateDialogHeader(): void {
        this.translate
            .get([
                'activities_new_times',
                'activities_edit_times',
                'activities_view_locked',
                'button_add',
                'button_done',
            ])
            .subscribe((translations: AngularTranslations) => {
                if (this.isNewActivityMode) {
                    this.dialogHeader = translations['activities_new_times'];
                    this.primaryButtonText = translations['button_add'];
                } else if (this.isLocked && !this.userCanOverrideLock) {
                    this.dialogHeader = translations['activities_view_locked'];
                    this.primaryButtonText = translations['button_done'];
                } else {
                    this.dialogHeader = translations['activities_edit_times'];
                    this.primaryButtonText = translations['button_done'];
                }
            });
    }

    async initializeTiles(): Promise<void> {
        await this.retrieveAndSetTiles();
        this.initialized = true;
        this.prepareUI(true);
    }

    async retrieveAndSetTiles(): Promise<void> {
        this.assignedTiles = await this.tileService.getTiles();
        // sort order
        this.assignedTiles.sort((a, b) => a.name.localeCompare(b.name));
    }

    onShow(): void {
        this.prepareUI();
    }

    onHide(): void {
        this.showDialogChange.emit(false);
    }

    isRegularUser(): boolean {
        // Return true if the current user does not have the TILE_CREATE permission
        return !(this.currentUser?.permissions.includes(UserPermission.TILE_CREATE) ?? false);
    }

    // Getter to determine if the user filter dropdown should be disabled
    get userFilterIsDisabled(): boolean {
        return !this.isPro || !this.isNewActivityMode || this.isRegularUser();
    }

    getLayerPlaceholder(layerIndex: number): string {
        return this.layers.find((layer) => layer.index === layerIndex)?.name ?? '';
    }

    private getLayerFilterValue(activity: ActivityDetailVm, layerIndex: number): Tile | undefined {
        // TODO the logic of this method highly relies on how the select options are set up and how p-select does the comparison
        //      a more reliable solution should be implemented with less hidden / implicit behavior

        // note: It is important that this is the same object instance as in the p-select options
        //       The component will relies on object comparison (same reference / object)
        const tile = this.assignedTiles.find((t) => t.id === activity.tileIds[layerIndex - 1]);
        if (tile !== undefined) {
            return tile;
        }

        // Fallback for not accessible tiles:
        // This will show the value correctly, but as it is not part of the options, make it impossible to be selected
        // again after change
        const tileViewForm = activity.tiles.find((tile) => tile.layerIndex === layerIndex);
        if (!tileViewForm && activity.tiles.length >= layerIndex) {
            console.error('Unexpected data: Tile not found for layer index:', layerIndex);
        }
        return tileViewForm ? (tileViewForm as Partial<Tile> as Tile) : undefined;
    }

    prepareUI(init = false): void {
        this.usersFilter = JSON.parse(JSON.stringify(this.analyzeComponent.activeUsersFilterOptions));
        this.layers = JSON.parse(JSON.stringify(this.analyzeComponent.layers));

        this.isLoading = false;

        const includeArchived = !this.isNewActivityMode;

        const act = this._selectedActivity;

        if (act && init) {
            this.selectedUserFilter = this.usersFilter.find((u) => u.id === this._selectedActivity?.userId);

            this.selectedLayer1Filter = this.getLayerFilterValue(act, 1);
            this.selectedLayer2Filter = this.getLayerFilterValue(act, 2);
            this.selectedLayer3Filter = this.getLayerFilterValue(act, 3);

            // TODO .clone() can be removed, when bi-directional binding works correctly all over the place.
            this.dateFrom = act.start.clone();
            this.dateTo = act.end.clone();
            this.note = act.note;
            this.isBilled = act.billed;
            this.isLocked = act.lock?.locked ?? false;
            this.userCanOverrideLock = act.lock?.userCanOverride ?? false;
            this.disabledEditing = this.isLocked && !this.userCanOverrideLock;
            this.lockReasonCodes = act.lock?.reasonCodes ?? [];
        } else if (!act && init) {
            this.selectedUserFilter = this.usersFilter.find((u) => u.id === this._currentUserId);
            this.selectedLayer1Filter = undefined;
            this.selectedLayer2Filter = undefined;
            this.selectedLayer3Filter = undefined;
            this.dateFrom = TimezonedDate.tzNow(-1, 'hour');
            this.dateTo = TimezonedDate.tzNow();
            this.note = '';
            this.isBilled = false;
            this.isLocked = false;
            this.userCanOverrideLock = false;
            this.lockReasonCodes = [];
            this.disabledEditing = false;
        }
        this.fromOffset = undefined;
        this.toOffset = undefined;
        this.timeZoneSwitchText = undefined;
        this.durationWarningText = undefined;

        if (this.dateFrom && this.dateTo && this.dateFrom.getApiUtcOffset() !== this.dateTo.getApiUtcOffset()) {
            this.fromOffset = 'UTC ' + this.dateFrom.getApiUtcOffset();
            this.toOffset = 'UTC ' + this.dateTo.getApiUtcOffset();
            const offsetDiff = this.dateFrom.getOffsetDiff(this.dateTo, 'hours');
            this.timeZoneSwitchText = this.translate.instant('activities_timezone_switch_text', { hours: offsetDiff });
        }

        if (this.usersFilter.length === 1) {
            this.selectedUserFilter = this.usersFilter[0];
        }

        // filtering that considers archived tiles based on includeArchived flag
        this.layer1Filter = this.assignedTiles.filter(
            (t) => t.layer === this.layers[0]?.id && (includeArchived || !t.archived),
        );

        if (
            this.selectedLayer1Filter &&
            !this.layer1Filter.find((l) => l.id === this.selectedLayer1Filter?.id) &&
            !this.assignedTiles.find((l) => l.id === this.selectedLayer1Filter?.id)
        ) {
            this.layer1Filter.push(this.selectedLayer1Filter);
        }

        this.layer2Filter = [
            ...(this.selectedLayer1Filter?.children
                ?.map((child) => this.assignedTiles.find((t) => t.id === child && (includeArchived || !t.archived))!)
                ?.filter((t) => t !== undefined) ?? []),
            ...this.assignedTiles.filter(
                (t) => t.layer === this.layers[1]?.id && (includeArchived || !t.archived) && t.type === TileType.Global,
            ),
        ];

        // if layer2Filter does not have selectedLayer2Filter, add it
        if (
            this.selectedLayer2Filter &&
            !this.layer2Filter.find((l) => l.id === this.selectedLayer2Filter?.id) &&
            !this.assignedTiles.find((l) => l.id === this.selectedLayer2Filter?.id)
        ) {
            this.layer2Filter.push(this.selectedLayer2Filter);
        }

        this.layer3Filter = [
            ...(this.selectedLayer2Filter?.children
                ?.map((child) => this.assignedTiles.find((t) => t.id === child && (includeArchived || !t.archived))!)
                ?.filter((t) => t !== undefined) ?? []),
            ...this.assignedTiles.filter(
                (t) => t.layer === this.layers[2]?.id && (includeArchived || !t.archived) && t.type === TileType.Global,
            ),
        ];

        if (
            this.selectedLayer3Filter &&
            !this.layer3Filter.find((l) => l.id === this.selectedLayer3Filter?.id) &&
            !this.assignedTiles.find((l) => l.id === this.selectedLayer3Filter?.id)
        ) {
            this.layer3Filter.push(this.selectedLayer3Filter);
        }

        // Sort the layer filters by name
        this.layer1Filter.sort((a, b) => a.name.localeCompare(b.name));
        this.layer2Filter.sort((a, b) => a.name.localeCompare(b.name));
        this.layer3Filter.sort((a, b) => a.name.localeCompare(b.name));

        this.checkTiles();
        this.validateFromTo(this.dateFrom, this.dateTo);
    }

    onTileSelectionChange(tile: Tile, index: number): void {
        if (!tile) {
            return; // Return early to prevent further execution
        }
        if (index === 0) {
            this.selectedLayer1Filter = tile;
        } else if (index === 1) {
            this.selectedLayer2Filter = tile;
        } else if (index === 2) {
            this.selectedLayer3Filter = tile;
        }

        // call resetLayerDropdowns first to remove any invalid tiles from the dropdowns
        this.resetLayerDropdowns();
        // // then call prepareUI to update the dropdowns for layer options
        this.prepareUI();
    }

    /**
     * This change hook is called by any change of a value in the component (ngModelChange)
     * and by any update of inputs (ngOnChanges)
     * @param change ngModelChange $event or ngOnChanges SimpleChanges object
     * @param source identifier of the source of this update (string): 'ngOnChanges' or form field names like 'billed'
     * @param data Optional data to be sent by the caller, e.g., layer index for tiles
     */
    public onChangeHook(change: Tile | Date | SimpleChanges | Event, source: string, data?: number): void {
        let validDuration = true;

        if (source === 'tile') {
            this.onTileSelectionChange(change as Tile, data!);
        } else if (source === 'dateFrom' || source === 'dateTo') {
            // convert date from date-picker back to TimezonedDate
            if (source === 'dateFrom') {
                this.dateFrom = TimezonedDate.toTimezonedDateSafe(this.dateFrom);
            } else {
                this.dateTo = TimezonedDate.toTimezonedDateSafe(this.dateTo);
            }
            validDuration = this.validateFromTo(this.dateFrom, this.dateTo);
        }

        this.saveDisabled = !this.areRequiredFieldsSet() || !validDuration;
    }

    /**
     * This method is called when the user changes the `dateFrom` or `dateTo` value via the datepicker or manual input.
     *
     * In contrast to onChangeHook() this method does not update the model / the stored values in the component.
     * While the user is editing, the date values may be temporarily invalid.
     * Therefore, persisting the values is deferred to the blur handler.
     * See `(onBlur)="onChangeHook($event, 'dateFrom')"`.
     *
     * This method only performs validation, updates the save-button state and warnings when both dates are valid.
     *
     * @param date The changed date value.
     * @param source The source of the change (either 'dateFrom' or 'dateTo').
     */
    public validateOnDateChange(date: Date, source: 'dateFrom' | 'dateTo'): void {
        let dateFrom = this.dateFrom;
        let dateTo = this.dateTo;
        if (source === 'dateFrom') {
            dateFrom = TimezonedDate.toTimezonedDateSafe(date);
        } else {
            dateTo = TimezonedDate.toTimezonedDateSafe(date);
        }
        if (!DataUtils.isValidDate(dateFrom) || !DataUtils.isValidDate(dateTo)) {
            return;
        }
        // on date change update warnings, when the dates have valid values
        const durationValid = this.validateFromTo(dateFrom, dateTo);
        // also enable or disable the save button
        this.saveDisabled = !this.areRequiredFieldsSet() || !durationValid;
    }

    private areRequiredFieldsSet(): boolean {
        return !!(this.selectedLayer1Filter && this.selectedUserFilter && this.dateFrom && this.dateTo);
    }

    /**
     * Validates the dateFrom and dateTo variables and their duration.
     * Displays a warning message if the duration is negative or too long.
     * @returns true if the dates are valid and the duration is not negative, false otherwise
     */
    private validateFromTo(dateFrom: TimezonedDate, dateTo: TimezonedDate): boolean {
        let negativeDuration = false;
        let invalidDate = false;

        if (DataUtils.isValidDate(dateFrom) && DataUtils.isValidDate(dateTo)) {
            const duration = this.getDuration();
            if (duration < 0) {
                negativeDuration = true;
                this.durationWarningText = this.translate.instant('activities_negative_duration_text');
            } else if (duration > 60 * 60 * 24 * 1000) {
                this.durationWarningText = this.translate.instant('activities_long_duration_text');
            } else {
                this.durationWarningText = undefined;
            }
        } else {
            this.durationWarningText = undefined;
            invalidDate = true;
        }
        return !negativeDuration && !invalidDate;
    }

    private getDuration(): number {
        if (!this.dateFrom || !this.dateTo) {
            throw new Error('Cannot calculate duration: dateFrom or dateTo is not defined.');
        }
        let start, end;
        if (this._selectedActivity) {
            start = this._selectedActivity.start.clone();
            end = this._selectedActivity.end.clone();
            start.assign(this.dateFrom, true);
            end.assign(this.dateTo, true);
        } else {
            start = new TimezonedDate(this.dateFrom);
            end = new TimezonedDate(this.dateTo);
        }
        return end.canonicalDiff(start);
    }

    resetLayerDropdowns(): void {
        // if the selectedLayer1Filter is valid and the layer2filter contains in it a tile that is not part of the assigned tiles, remove it
        if (
            this.selectedLayer1Filter &&
            this.selectedLayer2Filter &&
            !this.assignedTiles.find((l) => l.id === this.selectedLayer2Filter?.id)
        ) {
            this.selectedLayer2Filter = undefined;
        }
        // if the selectedLayer2Filter is valid and the layer3filter contains in it a tile that is not part of the assigned tiles, remove it
        if (
            this.selectedLayer2Filter &&
            this.selectedLayer3Filter &&
            !this.assignedTiles.find((l) => l.id === this.selectedLayer3Filter?.id)
        ) {
            this.selectedLayer3Filter = undefined;
        }
    }

    private hasChanges(activity: ActivityDetailVm): boolean {
        // TODO consider changing this to a more simple comparison with just an if, no copy, no equality comparison
        const copy = CloneUtils.cloneClass(activity);
        copy.start = this.dateFrom;
        copy.end = this.dateTo;
        copy.tileIds = this.getSelectedTilesId();
        copy.note = this.note;
        copy.billed = this.isBilled;
        copy.userId = this.selectedUserFilter?.id ?? 0;
        return isEqual(activity, copy);
    }

    public save(forceLockOverride: boolean = false): void {
        // Note: The component does not modify the existing date object but instead assigns a new one.
        //       As a result, this.dateFrom is not a TimezonedDate anymore when the component changed the value.
        if (this._selectedActivity) {
            if (this.hasChanges(this._selectedActivity)) {
                this.close();
                return;
            }
            if (this._selectedActivity.lock?.locked && !this.userCanOverrideLock) {
                return; // never save for regular users (userCanOverrideLock === false)
            }

            if (this._selectedActivity.lock?.locked && !forceLockOverride) {
                // Note: Display a confirmation dialog for admins / managers only if the lock is due to the billed status.
                const isBilledReason = this.lockReasonCodes.includes(LockReasonCode.BILLED_POLICY);
                if (this.userCanOverrideLock && isBilledReason) {
                    this.confirmationService.confirm({
                        header: this.translate.instant('activities_locked_edit_confirm_title'),
                        message: this.translate.instant('activities_locked_edit_confirm_description'),
                        icon: undefined,
                        acceptLabel: this.translate.instant('activities_locked_edit_confirm_okay'),
                        rejectLabel: this.translate.instant('activities_locked_edit_confirm_cancel'),
                        rejectButtonStyleClass: 'p-button-secondary p-button-text',
                        acceptButtonStyleClass: 'p-button-primary',
                        accept: () => this.save(true),
                    });
                    return;
                }
                // all other lock reasons are silently ignored for admins / managers (userCanOverrideLock === true)
            }

            // check if dates were modified and if so, set the seconds to zero:
            const fromDiff = this._selectedActivity.start.diff(this.dateFrom);
            const toDiff = this._selectedActivity.end.diff(this.dateTo);
            if (fromDiff != 0 || toDiff != 0) {
                this.dateFrom.setSeconds(0, 0);
                this.dateTo.setSeconds(0, 0);
            }

            this._selectedActivity.start.assign(this.dateFrom, true);
            this.dateFrom = this._selectedActivity.start;
            this._selectedActivity.end.assign(this.dateTo, true);
            this.dateTo = this._selectedActivity.end;
        } else {
            this.dateFrom.setSeconds(0, 0);
            this.dateTo.setSeconds(0, 0);
            this.dateFrom = new TimezonedDate(this.dateFrom);
            this.dateTo = new TimezonedDate(this.dateTo);
        }

        const activity: ActivityToCreate = {
            startDate: this.dateFrom,
            endDate: this.dateTo,
            tileIds: this.getSelectedTilesId(),
            note: this.note,
            billed: this.isBilled,
            userId: this.selectedUserFilter?.id ?? 0,
            customData: null,
        };

        if (!this._selectedActivity) {
            this.createActivity(activity);
        } else {
            const activityToUpdate: Activity = activityModelAddId(this._selectedActivity.id, activity);
            this.updateActivity(activityToUpdate);
        }
    }

    private onSaveSuccess(activityId: number): void {
        this.resetModalOptions();
        this.setLoading(false);
        this.saved.emit(activityId);
        this.showDialogChange.emit(false);
    }

    private updateActivity(activityToUpdate: Activity): void {
        this.activityService.update(activityToUpdate).subscribe({
            next: () => {
                this.onSaveSuccess(activityToUpdate.id);
            },
            error: (error) => {
                this.setLoading(false);
                console.error('Error updating activity:', error);
                this.toastService.error('standard_error');
            },
        });
    }

    private createActivity(activity: ActivityToCreate): void {
        this.setLoading(true);

        this.activityService.create(createActivity(activity)).subscribe({
            next: (activity) => {
                this.onSaveSuccess(activity.id);
            },
            error: (error: HttpErrorResponse) => {
                this.setLoading(false);
                if (error.status === 400) {
                    // 400 Bad Request
                    console.error('Validation error:', error.error);
                    if (error.status === 400 && error.error?.ValidationErrors) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        error.error.ValidationErrors.forEach((err: any) => {
                            if (err.constraints) {
                                const errorMessage = Object.values(err.constraints).join(', ');
                                this.toastService.errorNoTranslate('Validation Error', errorMessage);
                            }
                        });
                    }
                } else if (error.status === 500) {
                    console.error('Server error:', error.error);
                    this.toastService.error('standard_error');
                } else {
                    this.toastService.error('standard_error');
                    console.error('Error creating activity:', error);
                }
            },
        });
    }

    resetModalOptions(): void {
        this.selectedLayer1Filter = undefined;
        this.selectedLayer2Filter = undefined;
        this.selectedLayer3Filter = undefined;
        this.isBilled = false;
        this.selectedUserFilter = this.usersFilter.find((u) => u.id === this._currentUserId);
        this.dateFrom = TimezonedDate.tzNow(-1, 'hour');
        this.dateTo = TimezonedDate.tzNow();
        this.fromOffset = undefined;
        this.toOffset = undefined;
        this.timeZoneSwitchText = undefined;
        this.durationWarningText = undefined;
        this.note = '';
    }

    confirmDeleteActivity(): void {
        this.confirmationService.confirm({
            message: this.translate.instant('activity_del_confirm'),
            header: this.translate.instant('delete_group_confirm_header'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: this.translate.instant('standard_yes'),
            rejectLabel: this.translate.instant('standard_no'),
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-secondary p-button-text',
            accept: () => {
                this.deleteActivity();
            },
            reject: () => {
                console.log('Delete activity canceled');
            },
        });
    }

    close(): void {
        this.resetModalOptions();
        this.showDialog = false;
    }

    deleteActivity(): void {
        this.isLoading = true;
        this.loadingService.setLoading(true);
        if (this._selectedActivity) {
            const activityId = this._selectedActivity.id;
            this.activityService.delete(activityId).subscribe(() => {
                this.isLoading = false;
                this.loadingService.setLoading(false);
                this.deleted.emit(activityId);
                this.showDialogChange.emit(false);
            });
        }
    }

    private setLoading(loading: boolean): void {
        this.loadingService.setLoading(loading);
        this.isLoading = loading;
    }

    private getSelectedTilesId(): number[] {
        const selectedTiles = [];
        if (this.selectedLayer1Filter && this.layer1Filter.find((l) => l.id === this.selectedLayer1Filter?.id)) {
            selectedTiles.push(this.selectedLayer1Filter.id);
        }
        if (this.selectedLayer2Filter && this.layer2Filter.find((l) => l.id === this.selectedLayer2Filter?.id)) {
            selectedTiles.push(this.selectedLayer2Filter.id);
        }
        if (this.selectedLayer3Filter && this.layer3Filter.find((l) => l.id === this.selectedLayer3Filter?.id)) {
            selectedTiles.push(this.selectedLayer3Filter.id);
        }

        return selectedTiles;
    }

    private checkTiles(): void {
        if (this.selectedLayer1Filter) {
            if (!this.layer1Filter.find((l) => l.id === this.selectedLayer1Filter?.id)) {
                this.selectedLayer1Filter = undefined;
            }
        }
        if (this.selectedLayer2Filter) {
            if (!this.layer2Filter.find((l) => l.id === this.selectedLayer2Filter?.id)) {
                this.selectedLayer2Filter = undefined;
                this.selectedLayer3Filter = undefined;
            }
        }
    }
}
